import React, { useState, useEffect } from "react";

// Helper function to format opened time (local)
const getOpenedTimeLabel = (startTime) => {
  if (!startTime) return null;

  const start = new Date(startTime);
  if (Number.isNaN(start.getTime())) return null;

  return start.toLocaleTimeString([], {
    hour: "numeric",
    minute: "2-digit",
  });
};

const ACTIVE_TABLE_STATUSES = new Set([
  "OCCUPIED",
  "REQUEST_BILL",
  "DRINK_REPEAT",
  "IDLE",
]);

const hexToRgb = (hexColor) => {
  const normalized = String(hexColor || "").trim();
  const hex = normalized.startsWith("#") ? normalized.slice(1) : normalized;

  if (hex.length !== 6) return null;

  const parsed = Number.parseInt(hex, 16);
  if (Number.isNaN(parsed)) return null;

  return {
    r: (parsed >> 16) & 255,
    g: (parsed >> 8) & 255,
    b: parsed & 255,
  };
};

const getContrastPalette = (backgroundColor) => {
  const rgb = hexToRgb(backgroundColor);
  if (!rgb) {
    return {
      primaryText: "#0f172a",
      secondaryText: "rgba(15, 23, 42, 0.78)",
      badgeBackground: "rgba(255, 255, 255, 0.24)",
      overlayBackground: "rgba(255, 255, 255, 0.08)",
      borderColor: "rgba(15, 23, 42, 0.12)",
      shadowColor: "rgba(15, 23, 42, 0.18)",
    };
  }

  const luminance =
    (0.2126 * rgb.r + 0.7152 * rgb.g + 0.0722 * rgb.b) / 255;
  const useDarkText = luminance > 0.62;

  return useDarkText
    ? {
        primaryText: "#0f172a",
        secondaryText: "rgba(15, 23, 42, 0.78)",
        badgeBackground: "rgba(255, 255, 255, 0.34)",
        overlayBackground: "rgba(255, 255, 255, 0.12)",
        borderColor: "rgba(15, 23, 42, 0.12)",
        shadowColor: "rgba(15, 23, 42, 0.18)",
      }
    : {
        primaryText: "#ffffff",
        secondaryText: "rgba(255, 255, 255, 0.88)",
        badgeBackground: "rgba(15, 23, 42, 0.16)",
        overlayBackground: "rgba(255, 255, 255, 0.08)",
        borderColor: "rgba(255, 255, 255, 0.24)",
        shadowColor: "rgba(15, 23, 42, 0.28)",
      };
};

const densityStyles = {
  spacious: {
    card: "min-h-[140px] px-2 py-3",
    dotOffset: "right-3 top-3",
    timerOffset: "left-3 top-3",
    tableId: "text-3xl",
    guestText: "text-xs",
    totalText: "text-sm",
    seatText: "text-xs",
    icon: "h-3.5 w-3.5",
    timerIcon: "h-3 w-3",
    timerText: "text-xs",
  },
  comfortable: {
    card: "min-h-[124px] px-2 py-2.5",
    dotOffset: "right-2.5 top-2.5",
    timerOffset: "left-2.5 top-2.5",
    tableId: "text-2xl",
    guestText: "text-xs",
    totalText: "text-sm",
    seatText: "text-xs",
    icon: "h-3.5 w-3.5",
    timerIcon: "h-3 w-3",
    timerText: "text-[11px]",
  },
  compact: {
    card: "min-h-[108px] px-1.5 py-2",
    dotOffset: "right-2 top-2",
    timerOffset: "left-2 top-2",
    tableId: "text-xl",
    guestText: "text-[11px]",
    totalText: "text-xs",
    seatText: "text-[11px]",
    icon: "h-3 w-3",
    timerIcon: "h-2.5 w-2.5",
    timerText: "text-[10px]",
  },
  "ultra-compact": {
    card: "min-h-[96px] px-1.5 py-1.5",
    dotOffset: "right-1.5 top-1.5",
    timerOffset: "left-1.5 top-1.5",
    tableId: "text-lg",
    guestText: "text-[10px]",
    totalText: "text-[11px]",
    seatText: "text-[10px]",
    icon: "h-2.5 w-2.5",
    timerIcon: "h-2.5 w-2.5",
    timerText: "text-[10px]",
  },
};

const TableCard = ({ table, density = "spacious" }) => {
  const ui = densityStyles[density] || densityStyles.spacious;
  const [timeElapsed, setTimeElapsed] = useState(
    getOpenedTimeLabel(table.orderTime),
  );
  const palette = getContrastPalette(table.backgroundColor);

  const shouldShowTimer =
    Boolean(table.orderTime) &&
    ACTIVE_TABLE_STATUSES.has(String(table.status || "").toUpperCase());

  // Sync immediately when order time changes.
  useEffect(() => {
    setTimeElapsed(getOpenedTimeLabel(table.orderTime));
  }, [table.orderTime]);

  // Update time label every minute for active tables.
  useEffect(() => {
    if (shouldShowTimer) {
      const interval = setInterval(() => {
        setTimeElapsed(getOpenedTimeLabel(table.orderTime));
      }, 60000); // Update every minute

      return () => clearInterval(interval);
    }
  }, [shouldShowTimer, table.orderTime]);

  const hasTimeBadge = shouldShowTimer && timeElapsed;

  const handleClick = () => {
    if (table.onClick) {
      table.onClick();
    }
  };

  return (
    <button
      type="button"
      onClick={handleClick}
      className={`
        pos-table-card pos-animate-in
        relative flex w-full flex-col items-center justify-center
        ${ui.card}
        ${hasTimeBadge ? "pt-5" : ""}
        focus:ring-indigo-400 dark:focus:ring-indigo-500 focus:ring-offset-slate-100 dark:focus:ring-offset-slate-900
      `}
      style={{
        backgroundColor: table.backgroundColor || "#4CAF50",
        borderColor: palette.borderColor,
        boxShadow: `0 18px 38px -28px ${palette.shadowColor}`,
      }}
    >
      <div className={`absolute ${ui.dotOffset}`}>
        <div
          className="h-3 w-3 rounded-full shadow-lg"
          style={{
            backgroundColor: palette.primaryText,
            boxShadow: `0 0 0 3px ${palette.badgeBackground}`,
          }}
        />
      </div>

      {hasTimeBadge && (
        <div className={`absolute ${ui.timerOffset}`}>
          <div
            className="flex items-center gap-1 rounded-lg px-2 py-0.5 backdrop-blur-sm shadow-sm"
            style={{ backgroundColor: palette.badgeBackground }}
          >
            <svg
              className={ui.timerIcon}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              style={{ color: palette.secondaryText }}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
            <span
              className={`${ui.timerText} font-semibold`}
              style={{ color: palette.secondaryText }}
            >
              {timeElapsed}
            </span>
          </div>
        </div>
      )}

      <div
        className={`${ui.tableId} text-center font-black tracking-tight`}
        style={{ color: palette.primaryText }}
      >
        {table.tableNo}
      </div>

      {table.displayName ? (
        <div
          className="mt-1 text-center text-[11px] font-semibold"
          style={{ color: palette.secondaryText }}
        >
          {table.displayName}
        </div>
      ) : null}

      {(table.covers > 0 || table.orderTotal > 0) && (
        <div className="mt-2 space-y-1 text-center">
          {table.covers > 0 && (
            <div
              className={`flex items-center justify-center gap-1.5 ${ui.guestText} font-bold`}
              style={{ color: palette.secondaryText }}
            >
              <svg
                className={ui.icon}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                />
              </svg>
              <span>
                {table.covers} {table.covers === 1 ? "guest" : "guests"}
              </span>
            </div>
          )}
          {table.orderTotal > 0 && (
            <div
              className={`${ui.totalText} font-black`}
              style={{ color: palette.primaryText }}
            >
              ${table.orderTotal.toFixed(2)}
            </div>
          )}
        </div>
      )}

      <div
        className="absolute inset-0 rounded-2xl transition-colors duration-200 hover:bg-white/10"
        style={{ backgroundColor: "transparent" }}
      />
      <div
        className="pointer-events-none absolute inset-0 rounded-2xl opacity-0 transition-opacity duration-200 hover:opacity-100"
        style={{ backgroundColor: palette.overlayBackground }}
      />
    </button>
  );
};

export default TableCard;
